import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon } from 'lucide-react';
import type { Course } from '../types/course';
import type { CalendarPalette } from '../data/courseThemes';
import { LT_WEEKDAY_FULL, LT_WEEKDAY_SHORT, buildCourseMonths, countSessions, formatRange } from '../utils/calendar';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.65,
    ease: 'easeOut'
  }
};
const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  },
  viewport: {
    once: true,
    margin: '-80px'
  }
};

/**
 * Kurso kalendorius. Be `palette` — originali baltai rožinė / auksinė gama
 * (artėjantis kursas). Su `palette` — spalvos iš pasirinktos temos.
 */
export function CourseCalendar({
  course,
  showStats = true,
  palette
}: {
  course: Course;
  showStats?: boolean;
  palette?: CalendarPalette;
}) {
  const months = buildCourseMonths(course.start, course.end, course.weekdays);
  const sessions = countSessions(course.start, course.end, course.weekdays);
  const cardStyle = palette ? {
    background: palette.cardBg,
    borderColor: palette.cardBorder
  } : undefined;
  const headingStyle = palette ? {
    color: palette.heading
  } : undefined;
  const mutedStyle = palette ? {
    color: palette.muted
  } : undefined;
  const accentStyle = palette ? {
    background: palette.accent
  } : undefined;
  const activeDayStyle = palette ? {
    background: palette.activeBg,
    color: palette.activeText
  } : undefined;
  const pillStyle = palette ? {
    background: palette.sectionBg,
    borderColor: palette.cardBorder
  } : undefined;
  return <div className="space-y-8" data-id="element-24" data-mp="32d3oorj96v37">
      {/* Date + days — unified card */}
      <motion.div {...fadeInUp} className={`rounded-2xl px-8 py-6 max-w-2xl mx-auto border ${palette ? '' : 'bg-white border-sand-dark/20'}`} style={cardStyle} data-id="element-25" data-mp="169fi22xvo4iu">
        <div className="flex items-center gap-3 justify-center mb-5" data-id="element-26" data-mp="1bad811p4q2uw">
          <CalendarIcon className={`w-5 h-5 flex-shrink-0 ${palette ? '' : 'text-gold-dark'}`} style={palette ? {
          color: palette.accent
        } : undefined} data-id="element-27" data-mp="1g6242v1xx1mg" />
          <p className={`text-2xl font-bold ${palette ? '' : 'text-mountain-darker'}`} style={headingStyle} data-id="element-28" data-mp="1u63cqwog5dch">
            {formatRange(course.start, course.end)}
          </p>
        </div>
        <div className={`w-12 h-px mx-auto mb-5 ${palette ? '' : 'bg-gold'}`} style={accentStyle} data-id="element-29" data-mp="23welcwj2sapp" />
        <div className="flex flex-wrap justify-center gap-2" data-id="element-30" data-mp="2cxvxy4x9xu9n">
          {course.weekdays.map(dow => <div key={dow} className={`flex items-center gap-2 border px-4 py-2 rounded-xl ${palette ? '' : 'bg-sand-lightest border-sand-dark/20'}`} style={pillStyle} data-id="element-31" data-mp="1aals49jazdm">
              <div className={`w-1.5 h-1.5 rounded-full ${palette ? '' : 'bg-gold'}`} style={accentStyle} data-id="element-32" data-mp="3og9vy5vl93l1" />
              <span className={`font-medium text-sm ${palette ? '' : 'text-mountain-darker'}`} style={headingStyle} data-id="element-33" data-mp="3jii4te2rplhw">
                {LT_WEEKDAY_FULL[dow]}
              </span>
            </div>)}
        </div>
      </motion.div>

      {/* Month grids */}
      <motion.div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
      once: true,
      margin: '-80px'
    }} data-id="element-34" data-mp="69n8mp0eo6g7">
        {months.map(month => <motion.div key={`${month.year}-${month.month}`} variants={fadeInUp} className={`border p-5 rounded-2xl ${palette ? '' : 'bg-white border-sand-dark/30'}`} style={cardStyle} data-id="element-35" data-mp="iia5eoqisc6f">
            <h3 className={`text-base font-bold mb-4 text-center tracking-wide ${palette ? '' : 'text-mountain-darker'}`} style={headingStyle} data-id="element-36" data-mp="9joeo6aw9quk">
              {month.label}
            </h3>
            <div className="grid grid-cols-7 gap-1" data-id="element-37" data-mp="12954qxb1kvnm">
              {LT_WEEKDAY_SHORT.map(d => <div key={d} className={`text-center text-[10px] font-semibold pb-1 ${palette ? '' : 'text-mountain-dark/40'}`} style={mutedStyle} data-id="element-38" data-mp="16a77vp82xruv">
                  {d}
                </div>)}
              {Array.from({
            length: month.leadingBlanks
          }).map((_, i) => <div key={`blank-${i}`} className="aspect-square" data-id="element-39" data-mp="1px53okiibsda" data-mpi={i} />)}
              {month.days.map(({
            day,
            active
          }) => <div key={day} className={`aspect-square flex items-center justify-center rounded text-[11px] ${active ? 'font-bold' : 'font-medium'} ${palette ? '' : active ? 'bg-gold text-white' : 'text-mountain-dark/50'}`} style={palette ? active ? activeDayStyle : mutedStyle : undefined} data-id="element-40" data-mp="1z8ssg8fsm8b0">
                  {day}
                </div>)}
            </div>
          </motion.div>)}
      </motion.div>

      {showStats && <motion.div className={`flex flex-col sm:flex-row items-stretch justify-center border rounded-2xl overflow-hidden ${palette ? '' : 'bg-white border-sand-dark/30'}`} style={cardStyle} variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
      once: true,
      margin: '-80px'
    }} data-id="element-41" data-mp="1lamdoamjcz17">
          {[{
        value: String(course.weeks),
        label: 'Savaičių',
        sub: 'Programos trukmė'
      }, {
        value: String(sessions),
        label: 'Užsiėmimų',
        sub: 'Iš viso sesijų'
      }, {
        value: String(course.academicHours),
        label: 'Akademinių val.',
        sub: 'Po 2–2.5 val.'
      }].map((stat, i, arr) => <motion.div key={stat.label} variants={fadeInUp} className="flex-1 flex flex-col items-center text-center px-8 py-8 relative" data-id="element-42" data-mp="2v0npqpeo3edi" data-mpi={i}>
              {i < arr.length - 1 && <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2" style={{
          width: 1,
          height: '55%',
          background: palette ? palette.cardBorder : 'rgb(var(--sand-dark) / 0.4)'
        }} data-id="element-43" data-mp="22axld82u7qul" />}
              <span className={`font-bold leading-none mb-2 ${palette ? '' : 'text-gold-dark'}`} style={{
          fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
          ...(palette ? {
            color: palette.accent
          } : {})
        }} data-id="element-44" data-mp="1pw8yio08jy9n">
                {stat.value}
              </span>
              <p className={`text-sm font-semibold uppercase tracking-wide mb-1 ${palette ? '' : 'text-mountain-darker'}`} style={headingStyle} data-id="element-45" data-mp="124tqcels2mqh">
                {stat.label}
              </p>
              <p className={`text-xs ${palette ? '' : 'text-mountain-dark/50'}`} style={mutedStyle} data-id="element-46" data-mp="t56tm63zu53r">
                {stat.sub}
              </p>
            </motion.div>)}
        </motion.div>}
    </div>;
}